import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Persona } from '../types/persona';
import { PersonaAvatar } from '../components/personas/PersonaAvatar';
import { PersonaCapabilityMeter } from '../components/personas/PersonaCapabilityMeter';
import * as personaService from '../services/personaService';
import { useTheme } from '../contexts/ThemeContext';
import { getCapabilityColor } from '../utils/colorUtils';
import { useAuth } from '../contexts/AuthContext';
import { DeleteConfirmationModal } from '../components/common/DeleteConfirmationModal';
import { NewConversationButton } from '../components/common/NewConversationButton';
import { useToast } from '../contexts/ToastContext';
import { FiEdit3, FiMessageSquare, FiGitBranch, FiTrash2, FiArrowLeft, FiCalendar, FiUser, FiTag, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle, FiDownload } from 'react-icons/fi';
import { PersonaMarkdown } from '../components/personas/PersonaMarkdown';

// Capabilities can be legacy numbers or CapabilityEntry objects — normalize to number
function capScore(v: any): number {
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && typeof v.score === 'number') return v.score;
  return 0;
}

export const PersonaDetail: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'capabilities' | 'patterns' | 'usage' | 'config' | 'prompt'>('overview');
  const { user } = useAuth();
  const { showToast } = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Function to export persona data
  const exportPersona = (persona: Persona) => {
    // Create a formatted JSON object with the persona data
    const exportData = {
      id: persona.id,
      name: persona.name,
      shortDescription: persona.shortDescription,
      fullDescription: persona.fullDescription,
      primarySpecialization: persona.primarySpecialization,
      tags: persona.tags,
      capabilities: persona.capabilities,
      patterns: persona.patterns,
      antipatterns: persona.antipatterns,
      tethics: persona.tethics,
      allowedTools: persona.allowedTools,
      prompt: persona.prompt,
      created_at: persona.created_at,
      updated_at: persona.updated_at
    };

    // Convert to JSON string with pretty formatting
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create a blob and download link
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `persona_${persona.id}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast("Success", "Persona exported successfully", "success");
  };
  
  // Add this function to handle persona deletion
  const handleDeletePersona = async () => {
    if (!persona) return;

    setIsDeleting(true);
    try {
      await personaService.deletePersona(persona.id);
      await queryClient.invalidateQueries(['personas']);
      await queryClient.invalidateQueries(['personaTags']);

      showToast(`Persona "${persona.name}" has been deleted`, 'success');

      // Go back to wherever the user came from. Fallback to /personas if there's
      // no history (e.g. deep-linked into the detail page).
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/personas');
      }
    } catch (error) {
      console.error('Error deleting persona:', error);
      showToast('Failed to delete persona. You may not have permission to perform this action.', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Fetch persona details
  const { data: persona, isLoading, error } = useQuery(
    ['persona', personaId],
    () => personaId ? personaService.getPersona(personaId) : Promise.reject('No persona ID'),
    { 
      enabled: !!personaId,
      retry: false,
      staleTime: 60000, // 1 minute
      onError: (err) => console.error('Error fetching persona:', err)
    }
  );
  
  // Fetch persona metrics
  const { data: metrics } = useQuery(
    ['personaMetrics', personaId],
    () => personaId ? personaService.getPersonaMetrics(personaId) : Promise.reject('No persona ID'),
    { 
      enabled: !!personaId && !!persona,
      retry: false,
      staleTime: 300000, // 5 minutes
      onError: (err) => console.error('Error fetching metrics:', err)
    }
  );
  
  // Fetch persona history
  const { data: history } = useQuery(
    ['personaHistory', personaId],
    () => personaId ? personaService.getPersonaHistory(personaId) : Promise.reject('No persona ID'),
    { 
      enabled: !!personaId && !!persona,
      retry: false,
      staleTime: 300000, // 5 minutes
      onError: (err) => console.error('Error fetching history:', err)
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !persona) {
    return (
      <div className={`${darkMode ? 'bg-red-900 bg-opacity-20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'} px-4 py-3 rounded relative border`} role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> Failed to load persona details.</span>
        <button 
          className={`mt-3 ${darkMode ? 'bg-red-800 bg-opacity-50 text-red-200 hover:bg-opacity-70' : 'bg-red-100 text-red-800 hover:bg-red-200'} px-3 py-1 rounded flex items-center`}
          onClick={() => navigate('/personas')}
        >
          <FiArrowLeft className="mr-1" />
          Return to Personas
        </button>
      </div>
    );
  }

  // Get primary specialization from tags if not explicitly set
  const primarySpecialization = persona.primarySpecialization || 
    (persona.tags && persona.tags.length > 0 ? persona.tags[0] : 'general');

  const handleEditPersona = () => {
    // Redirect to Persona Forge with the current persona ID for editing
    navigate(`/personas/forge?personaId=${persona.id}`);
  };

  const handleRemixPersona = () => {
    // Navigate to Persona Forge with remix state
    navigate(`/personas/forge`, { state: { remixPersonaId: persona.id } });
  };


  const handleBackToPersonas = () => {
    navigate('/personas');
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button 
        onClick={handleBackToPersonas}
        className={`flex items-center ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
      >
        <FiArrowLeft className="mr-1" />
        <span>Back to Personas</span>
      </button>

      {/* Hero section with persona details */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg overflow-hidden transition-colors duration-200 relative`}>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500 to-purple-600 rounded-full opacity-10 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-green-500 to-cyan-600 rounded-full opacity-10 transform -translate-x-1/2 translate-y-1/2"></div>
        
        <div className={`${darkMode ? 'bg-gradient-to-r from-blue-900 to-indigo-900 bg-opacity-30 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-gray-200'} p-6 sm:p-8 border-b relative z-10`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <div className="relative">
              <PersonaAvatar
                name={persona.name}
                specialization={primarySpecialization}
                avatarUrl={persona.avatarUrl}
                size="large"
              />
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center border-2 border-white dark:border-gray-900">
                <span className="transform rotate-45">✓</span>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 sm:ml-6 flex-grow">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                <h1 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">{persona.name}</h1>
                <div className="mt-2 sm:mt-0 flex flex-wrap gap-2">
                  <NewConversationButton persona={persona} variant="ghost" />
                  <button
                    onClick={handleEditPersona}
                    className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors flex items-center`}
                  >
                    <FiEdit3 className="mr-1.5" />
                    Edit
                  </button>
                  <button
                    onClick={handleRemixPersona}
                    className={`px-4 py-2 ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded-md transition-colors flex items-center`}
                  >
                    <FiGitBranch className="mr-1.5" />
                    Remix
                  </button>
                  
                  {/* Export button */}
                  <button
                    onClick={() => persona && exportPersona(persona)}
                    className={`px-4 py-2 ${darkMode ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-cyan-500 hover:bg-cyan-600'} text-white rounded-md transition-colors flex items-center`}
                  >
                    <FiDownload className="mr-1.5" />
                    Export
                  </button>
                
                  {/* Only show delete button to admin users */}
                  {user?.is_superuser && (
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className={`px-4 py-2 ${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} text-white rounded-md transition-colors flex items-center`}
                    >
                      <FiTrash2 className="mr-1.5" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'} flex items-center`}>
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                {primarySpecialization}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {persona.tags.map((tag, index) => (
                  <span
                    key={`tag-${index}`}
                    className={`px-3 py-1 ${darkMode ? 'bg-blue-900 bg-opacity-50 text-blue-300' : 'bg-blue-100 text-blue-800'} text-sm rounded-full transition-colors duration-200 hover:scale-105 transform`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Metrics summary */}
          {metrics && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-4 rounded-lg shadow-sm text-center`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{metrics.successRate}%</div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center justify-center`}>
                  <FiCheckCircle className="mr-1" />
                  Success Rate
                </div>
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-4 rounded-lg shadow-sm text-center`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{metrics.avgResponseTime}</div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center justify-center`}>
                  <FiClock className="mr-1" />
                  Avg Response
                </div>
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-4 rounded-lg shadow-sm text-center`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{metrics.usageCount}</div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center justify-center`}>
                  <FiMessageSquare className="mr-1" />
                  Times Used
                </div>
              </div>
              <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-4 rounded-lg shadow-sm text-center`}>
                <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{metrics.sessionCount || 0}</div>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center justify-center`}>
                  <FiUser className="mr-1" />
                  Sessions
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation tabs */}
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex overflow-x-auto">
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'overview'
                  ? darkMode 
                    ? 'border-b-2 border-green-500 text-green-400 bg-green-900 bg-opacity-20' 
                    : 'border-b-2 border-green-500 text-green-600 bg-green-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Overview
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'capabilities'
                  ? darkMode 
                    ? 'border-b-2 border-purple-500 text-purple-400 bg-purple-900 bg-opacity-20' 
                    : 'border-b-2 border-purple-500 text-purple-600 bg-purple-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('capabilities')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Capabilities
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'patterns'
                  ? darkMode 
                    ? 'border-b-2 border-amber-500 text-amber-400 bg-amber-900 bg-opacity-20' 
                    : 'border-b-2 border-amber-500 text-amber-600 bg-amber-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('patterns')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Patterns & Ethics
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'usage'
                  ? darkMode 
                    ? 'border-b-2 border-blue-500 text-blue-400 bg-blue-900 bg-opacity-20' 
                    : 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('usage')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Usage History
              </div>
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'prompt'
                  ? darkMode 
                    ? 'border-b-2 border-indigo-500 text-indigo-400 bg-indigo-900 bg-opacity-20' 
                    : 'border-b-2 border-indigo-500 text-indigo-600 bg-indigo-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('prompt')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                System Prompt
              </div>
            </button>
            
            <button
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeTab === 'config'
                  ? darkMode 
                    ? 'border-b-2 border-cyan-500 text-cyan-400 bg-cyan-900 bg-opacity-20' 
                    : 'border-b-2 border-cyan-500 text-cyan-600 bg-cyan-50'
                  : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('config')}
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Configuration
              </div>
            </button>
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Character Bible */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Character Bible
                </h2>
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-5 rounded-lg border shadow-inner`}>
                  {/* Debug log to see persona structure */}
                  {console.log("Persona data structure:", persona)}
                  <PersonaMarkdown 
                    content={
                      persona.fullDescription || 
                      persona.character_bible || 
                      (persona.data && typeof persona.data === 'object' ? persona.data.character_bible : null) ||
                      (persona.data && typeof persona.data === 'object' && persona.data.engineering_profile ? 
                        (persona.data.engineering_profile.current_state ? 
                          persona.data.engineering_profile.current_state.character_bible : null) : null) ||
                      persona.shortDescription || 
                      persona.prompt || 
                      "No character bible available."
                    }
                    size="medium"
                  />
                </div>
              </div>

              {/* Capability summary */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Key Capabilities
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(persona.capabilities).slice(0, 4).map(([key, value]) => (
                    <PersonaCapabilityMeter
                      key={key}
                      label={key.charAt(0).toUpperCase() + key.slice(1)}
                      value={capScore(value)}
                      max={10}
                    />
                  ))}
                </div>
                <button
                  className={`mt-4 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} text-sm font-medium flex items-center`}
                  onClick={() => setActiveTab('capabilities')}
                >
                  View all capabilities
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>

              {/* Tools */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Allowed Tools
                </h2>
                <div className="flex flex-wrap gap-2">
                  {persona.allowedTools && persona.allowedTools.length > 0 ? (
                    persona.allowedTools.map((tool, index) => (
                      <span
                        key={`tool-${index}`}
                        className={`px-3 py-1 ${darkMode ? 'bg-green-900 bg-opacity-50 text-green-300 border border-green-800' : 'bg-green-100 text-green-700 border border-green-200'} text-sm rounded-full flex items-center`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {tool}
                      </span>
                    ))
                  ) : (
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>No specific tools assigned</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'capabilities' && (
            <div className="space-y-8">
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Capabilities
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    {Object.entries(persona.capabilities).map(([key, value]) => (
                      <PersonaCapabilityMeter
                        key={key}
                        label={key.charAt(0).toUpperCase() + key.slice(1)}
                        value={capScore(value)}
                        max={10}
                      />
                    ))}
                  </div>
                  <div>
                    <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-5 rounded-lg border shadow-inner`}>
                      <h3 className={`font-medium text-lg mb-3 ${darkMode ? 'text-gray-200' : ''}`}>Capability Descriptions</h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Creativity</dt>
                          <dd className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ability to generate novel ideas and solutions</dd>
                        </div>
                        <div>
                          <dt className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Technical</dt>
                          <dd className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Knowledge of technical concepts and implementation details</dd>
                        </div>
                        <div>
                          <dt className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Analytical</dt>
                          <dd className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ability to analyze problems and evaluate solutions</dd>
                        </div>
                        <div>
                          <dt className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Communication</dt>
                          <dd className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Clarity and effectiveness in expressing ideas</dd>
                        </div>
                      </dl>
                    </div>

                    {/* Capability visualization */}
                    <div className={`mt-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-5 rounded-lg border shadow-md`}>
                      <h3 className={`font-medium text-lg mb-3 ${darkMode ? 'text-gray-200' : ''}`}>Capability Balance</h3>
                      <div className="h-64 flex items-center justify-center">
                        <div className="relative w-full h-full max-w-xs mx-auto">
                          {/* Simple capability visualization */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className={`w-full h-full ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'} rounded-full`}></div>
                          </div>
                          
                          {Object.entries(persona.capabilities).length > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div 
                                className={`${darkMode ? 'bg-blue-600 bg-opacity-40' : 'bg-blue-500 bg-opacity-40'} rounded-full`}
                                style={{ 
                                  width: `${Math.max(...Object.values(persona.capabilities).map(capScore)) * 10}%`,
                                  height: `${Math.max(...Object.values(persona.capabilities).map(capScore)) * 10}%` 
                                }}
                              ></div>
                            </div>
                          )}
                          
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                              <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                {(Object.values(persona.capabilities).map(capScore).reduce((a, b) => a + b, 0) /
                                 Math.max(1, Object.values(persona.capabilities).length)).toFixed(1)}
                              </div>
                              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Average Rating</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'patterns' && (
            <div className="space-y-8">
              <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Patterns, Antipatterns & Technical Ethics
              </h2>
              
              {/* Three-column layout for patterns, antipatterns, and tech ethics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Patterns column */}
                <div>
                  <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-green-300' : 'text-green-700'} flex items-center`}>
                    <FiCheckCircle className="mr-1.5" />
                    Patterns
                  </h3>
                  <div className={`${darkMode ? 'bg-green-900 bg-opacity-20 border-green-800' : 'bg-green-50 border-green-200'} p-5 rounded-lg border h-full shadow-inner`}>
                    {persona.patterns && persona.patterns.length > 0 ? (
                      <ul className="space-y-2">
                        {persona.patterns.map((pattern, index) => (
                          <li key={index} className="flex items-start">
                            <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${darkMode ? 'bg-green-800 text-green-200' : 'bg-green-200 text-green-800'} mr-3 flex-shrink-0`}>
                              ✓
                            </span>
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{pattern}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-8">
                        <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>No patterns defined</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Antipatterns column */}
                <div>
                  <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-red-300' : 'text-red-700'} flex items-center`}>
                    <FiXCircle className="mr-1.5" />
                    Antipatterns
                  </h3>
                  <div className={`${darkMode ? 'bg-red-900 bg-opacity-20 border-red-800' : 'bg-red-50 border-red-200'} p-5 rounded-lg border h-full shadow-inner`}>
                    {persona.antipatterns && persona.antipatterns.length > 0 ? (
                      <ul className="space-y-2">
                        {persona.antipatterns.map((antipattern, index) => (
                          <li key={index} className="flex items-start">
                            <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${darkMode ? 'bg-red-800 text-red-200' : 'bg-red-200 text-red-800'} mr-3 flex-shrink-0`}>
                              ✗
                            </span>
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{antipattern}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-8">
                        <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>No antipatterns defined</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Technical Ethics column */}
                <div>
                  <h3 className={`text-lg font-medium mb-3 ${darkMode ? 'text-blue-300' : 'text-blue-700'} flex items-center`}>
                    <FiAlertCircle className="mr-1.5" />
                    Technical Ethics
                  </h3>
                  <div className={`${darkMode ? 'bg-blue-900 bg-opacity-20 border-blue-800' : 'bg-blue-50 border-blue-200'} p-5 rounded-lg border h-full shadow-inner`}>
                    {persona.tethics && persona.tethics.length > 0 ? (
                      <ul className="space-y-2">
                        {persona.tethics.map((tethic, index) => (
                          <li key={index} className="flex items-start">
                            <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-200 text-blue-800'} mr-3 flex-shrink-0`}>
                              !
                            </span>
                            <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tethic}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full py-8">
                        <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>No technical ethics defined</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'usage' && (
            <div className="space-y-8">
              <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Usage History
              </h2>
              
              {history && history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((entry, index) => (
                    <div key={`history-${index}`} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-5 rounded-lg border hover:shadow-md transition-shadow`}>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3">
                        <div>
                          <h3 className="font-medium flex items-center">
                            <FiMessageSquare className="mr-1.5 text-blue-500" />
                            {entry.session_name || `Session ${entry.session_id?.substring(0, 8) || 'Unknown'}`}
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center mt-1`}>
                            <FiCalendar className="mr-1.5" />
                            {new Date(entry.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <div className="mt-2 sm:mt-0">
                          <span className={`px-3 py-1 text-xs rounded-full flex items-center inline-block ${
                            entry.success 
                              ? darkMode ? 'bg-green-900 bg-opacity-50 text-green-300 border border-green-800' : 'bg-green-100 text-green-800 border border-green-200' 
                              : darkMode ? 'bg-red-900 bg-opacity-50 text-red-300 border border-red-800' : 'bg-red-100 text-red-800 border border-red-200'
                          }`}>
                            {entry.success ? (
                              <>
                                <FiCheckCircle className="mr-1" />
                                Success
                              </>
                            ) : (
                              <>
                                <FiXCircle className="mr-1" />
                                Failed
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} border-l-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'} pl-3 py-1`}>{entry.task || 'No task description'}</p>
                      
                      {entry.artifacts && entry.artifacts.length > 0 && (
                        <div className="mt-3">
                          <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2 flex items-center`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            Artifacts:
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {entry.artifacts.map((artifact, i) => (
                              <a 
                                key={`artifact-${i}`}
                                href={`/artifacts/${artifact.id}`}
                                className={`px-3 py-1 ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-200'} text-sm rounded transition-colors flex items-center`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                {artifact.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-8 rounded-lg border text-center shadow-inner`}>
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4 italic`}>No usage history available for this persona</p>
                  <NewConversationButton persona={persona} variant="ghost" className="mx-auto" />
                </div>
              )}
            </div>
          )}

          {activeTab === 'prompt' && (
            <div className="space-y-8">
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  System Prompt
                </h2>
                <div className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} p-5 rounded-lg border shadow-inner relative`}>
                  <button 
                    className={`absolute top-2 right-2 p-1.5 rounded-md ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-700'}`}
                    onClick={() => {
                      navigator.clipboard.writeText(persona.prompt);
                      showToast('System prompt copied to clipboard', 'success');
                    }}
                    title="Copy to clipboard"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-2M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                  <PersonaMarkdown 
                    content={persona.prompt}
                    size="medium"
                    maxHeight="600px"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-8">

              {/* Metadata */}
              <div>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-gray-200' : ''} flex items-center`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Metadata
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border shadow-md`}>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                      <FiTag className="mr-1.5" />
                      ID
                    </p>
                    <p className={`font-mono ${darkMode ? 'text-gray-300' : ''} mt-1 select-all`}>{persona.id}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border shadow-md`}>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                      <FiTag className="mr-1.5" />
                      Primary Specialization
                    </p>
                    <p className={darkMode ? 'text-gray-300' : ''}>{primarySpecialization}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border shadow-md`}>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                      <FiCalendar className="mr-1.5" />
                      Created
                    </p>
                    <p className={darkMode ? 'text-gray-300' : ''}>{new Date(persona.created_at).toLocaleString()}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border shadow-md`}>
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center`}>
                      <FiClock className="mr-1.5" />
                      Last Updated
                    </p>
                    <p className={darkMode ? 'text-gray-300' : ''}>{new Date(persona.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Delete confirmation modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePersona}
        itemType="persona"
        itemName={persona?.name}
        isDeleting={isDeleting}
        dangerLevel="medium"
      />
    </div>
  );
};
